import styled from '@emotion/styled';
import React from 'react';
import { __ } from '../../helpers/TranslationService';
import useFlights from '../../hooks/useFlights';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { AirplaneIcon } from '../Icons/AirplaneIcon';
import { RequestFormSelect } from '../RequestForm/RequestFormSelect';
import { SectionContainer, SectionHeader } from './CruiseInquiryForm.style';
import { IField } from './useInquiryFormValidation';
import { useActions } from '../../hooks/useActions';
import { ExternalLinkIcon } from '../Icons/ExternalLinkIcon';

const StyledRequestFormSelect = styled(RequestFormSelect)`
    select {
        text-align: left;
        text-align-last: left;
    }
`;

const StyledError = styled.p`
    color: ${(props) => props.theme.colors.watermelon['100']};
`;

export const ExternalLinkIconStyled = styled(ExternalLinkIcon)`
    fill: ${({ theme }) => theme.link.primary};
    position: relative;
    top: 2px;
    width: 16px;
    height: 16px;
    margin-left: 6px;
    &:hover {
        color: ${({ theme }) => theme.link.primaryHover};
    }
`;

export const LinkWithoutFlight = styled.a`
    color: ${({ theme }) => theme.link.primary};
    font-weight: 600;
    font-size: 14px;
    line-height: 22px;
    display: inline-block;
    margin-top: 8px;
    &:hover {
        color: ${({ theme }) => theme.link.primaryHover};
        ${ExternalLinkIconStyled} {
            fill: ${({ theme }) => theme.link.primaryHover};
        }
    }
`;

export const CruiseInquiryFlightSelection = ({
    flightField,
    isAllInclusive,
}: {
    flightField: IField;
    isAllInclusive?: boolean;
}) => {
    const { pageData, requestForm, cruisePage } = useTypedSelector((state) => state);
    const { alternativeCruise } = cruisePage;
    const { setAirportCode, setFlightClass } = useActions();

    const handleSetAirportCode = (value: string) => {
        const parsedFlightSelectValue = value.split('-');
        const airportCode = parsedFlightSelectValue?.[0] || '';
        const flightClass = parsedFlightSelectValue?.[1] || '';

        setFlightClass(flightClass);
        setAirportCode(airportCode);
    };

    const airports = useFlights({
        includesFlight: cruisePage.includesFlight,
        cabins: cruisePage.cabins,
        selectedCabin: cruisePage.selectedCabin,
        rates: cruisePage.rates,
        currency: pageData.appConfig.defaultCurrency,
        cabinNid: requestForm.cabinNid,
        cruisePageAirports: cruisePage.airports,
        selectedSail: cruisePage.selectedSailIndex,
        sailNid: requestForm.sailNid,
        passengersNum: requestForm?.passengers?.length,
        isAllInclusive,
    });

    const airportOptions = airports.options;
    const isInvalidField = flightField.control.isDirty && !flightField.control.isValid;
    const selectedAirportWithClass = requestForm.selectedFlightClass
        ? `${requestForm.selectedAirportCode}-${requestForm.selectedFlightClass}`
        : requestForm.selectedAirportCode;

    return (
        <SectionContainer>
            <SectionHeader>{__('cruiseData.flightMandatory', 'dreamlines')}</SectionHeader>
            <StyledRequestFormSelect
                key={requestForm.selectedAirportCode}
                id="reqform-v2-flight"
                defaultValue={selectedAirportWithClass || ''}
                icon={<AirplaneIcon width={16} height={16} />}
                options={airportOptions}
                onChange={handleSetAirportCode}
                hasErrors={isInvalidField}
            />
            {alternativeCruise?.includesFlight === false && (
                <LinkWithoutFlight href={alternativeCruise?.url}>
                    {__('To Cruise without Flight', 'dreamlines')}
                    <ExternalLinkIconStyled />
                </LinkWithoutFlight>
            )}
            {isInvalidField && <StyledError> {flightField.control.errors} </StyledError>}
        </SectionContainer>
    );
};
